<template>
    <OffsetBackground v-if="useOffset">
        <slot />
    </OffsetBackground>
    <div v-else>
        <slot />
    </div>
</template>

<script setup lang="ts">
/**
 * ConditionalOffsetBackground Component
 *
 * Conditionally wraps content in OffsetBackground based on a boolean prop.
 * When useOffset is true, content is wrapped in OffsetBackground (which provides
 * a white background offset from the left edge). When false, content is wrapped
 * in a plain, unstyled div.
 *
 * Use Case:
 * When you need to conditionally apply the offset background styling based on
 * page context, device type, or other conditions. This is particularly useful
 * for navigation components that may or may not need the offset background
 * depending on the page layout.
 *
 * Example usage:
 * ```vue
 * <ConditionalOffsetBackground :use-offset="isDesktop">
 *   <Navigation />
 * </ConditionalOffsetBackground>
 * ```
 */
import OffsetBackground from '~~/layers/base/components/shared/OffsetBackground.vue'

interface Props {
    /** When true, wraps content in OffsetBackground. When false, uses a plain unstyled div. */
    useOffset?: boolean
}

withDefaults(defineProps<Props>(), {
    useOffset: true,
})
</script>
